import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Link, RefreshCw, Shield, Trash2, UserPlus, Users } from 'lucide-react';
import { DASHBOARD_TABS, getDefaultTabsForRole, getTabLabel, sanitizeTabsForRole } from './dashboardAccess';
import { createDashboardInvite, deleteDashboardUser, isSupabaseConfigured, listDashboardUsers, updateDashboardUserAccess } from './supabaseClient';
import { buildDashboardInviteUrl, isUsingLocalDashboardBase } from './urlHelpers';

const ROLE_OPTIONS = [
  { value: 'analytics_viewer', label: 'Analytics viewer' },
  { value: 'model', label: 'Model / social manager' },
  { value: 'admin', label: 'Admin/internal' }
];

const formatDate = (value) => {
  if (!value) return 'Unknown';
  return new Date(value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

function AdminAccessPanel({ session, onCurrentUserUpdated }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('analytics_viewer');
  const [expiresInDays, setExpiresInDays] = useState(14);
  const [inviteLink, setInviteLink] = useState('');
  const [status, setStatus] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [savingUserId, setSavingUserId] = useState('');
  const [deletingUserId, setDeletingUserId] = useState('');

  const currentUserId = session?.user?.id;

  const loadUsers = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setUsers([]);
      return;
    }

    setIsLoadingUsers(true);
    try {
      const rows = await listDashboardUsers({ accessToken: session?.access_token });
      setUsers(rows || []);
    } catch (error) {
      setStatus(`${error.message}. If this is the first deploy after the update, run npx supabase db push.`);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateInvite = async () => {
    setIsCreating(true);
    setStatus('');
    setInviteLink('');

    try {
      if (!isSupabaseConfigured) {
        const previewToken = `preview-${crypto.randomUUID()}`;
        setInviteLink(buildDashboardInviteUrl(previewToken));
        setStatus('Preview link generated. Configure Supabase env vars and run the SQL migration to create real invites.');
        return;
      }

      const expiresAt = new Date(Date.now() + Number(expiresInDays) * 86400000).toISOString();
      const token = await createDashboardInvite({
        accessToken: session?.access_token,
        email,
        role,
        expiresAt
      });

      setInviteLink(buildDashboardInviteUrl(token));
      setStatus(isUsingLocalDashboardBase()
        ? 'Invite created, but this link uses localhost. Add REACT_APP_DASHBOARD_URL to use the production dashboard URL.'
        : 'Invite created. Send this link to the invited user.');
    } catch (error) {
      setStatus(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const copyInvite = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setStatus('Invite link copied.');
  };

  const saveUserAccess = async (user, nextRole, nextTabs) => {
    if (!isSupabaseConfigured) {
      setStatus('Connect Supabase before managing production users.');
      return;
    }

    const isSelf = user.id === currentUserId;
    const sanitizedTabs = sanitizeTabsForRole(nextTabs, nextRole);

    if (isSelf && nextRole !== 'admin') {
      setStatus('You cannot remove your own admin role.');
      return;
    }

    if (isSelf && !sanitizedTabs.includes('adminAccess')) {
      setStatus('You cannot hide your own Admin Access tab.');
      return;
    }

    setSavingUserId(user.id);
    setStatus('');

    try {
      const updatedUser = await updateDashboardUserAccess({
        accessToken: session?.access_token,
        userId: user.id,
        role: nextRole,
        tabs: sanitizedTabs
      });

      if (!updatedUser) throw new Error('No user was returned after saving.');

      setUsers((currentUsers) => currentUsers.map((row) => (
        row.id === updatedUser.id ? updatedUser : row
      )));

      if (updatedUser.id === currentUserId) {
        onCurrentUserUpdated?.(updatedUser);
      }

      setStatus(`Updated access for ${updatedUser.email || updatedUser.name || 'user'}.`);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setSavingUserId('');
    }
  };

  const handleRoleChange = (user, nextRole) => {
    const currentTabs = user.tabs?.length ? user.tabs : getDefaultTabsForRole(user.role);
    const tabsForRole = nextRole === 'admin' && !currentTabs.includes('adminAccess')
      ? [...currentTabs, 'adminAccess']
      : currentTabs;
    saveUserAccess(user, nextRole, tabsForRole);
  };

  const handleTabToggle = (user, tabKey) => {
    const currentTabs = user.tabs?.length ? user.tabs : getDefaultTabsForRole(user.role);
    const isSelected = currentTabs.includes(tabKey);
    const nextTabs = isSelected
      ? currentTabs.filter((currentTab) => currentTab !== tabKey)
      : [...currentTabs, tabKey];
    saveUserAccess(user, user.role, nextTabs);
  };

  const handleDeleteUser = async (user) => {
    if (!isSupabaseConfigured) {
      setStatus('Connect Supabase before deleting production users.');
      return;
    }

    if (user.id === currentUserId) {
      setStatus('You cannot delete your own admin account.');
      return;
    }

    const userLabel = user.email || user.name || 'this user';
    const confirmed = window.confirm(`Delete ${userLabel}? This removes their login and dashboard access.`);
    if (!confirmed) return;

    setDeletingUserId(user.id);
    setStatus('');

    try {
      await deleteDashboardUser({
        accessToken: session?.access_token,
        userId: user.id
      });

      setUsers((currentUsers) => currentUsers.filter((row) => row.id !== user.id));
      setStatus(`Deleted ${userLabel}.`);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setDeletingUserId('');
    }
  };

  return (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Admin access</p>
      <h2 className="text-2xl font-serif text-white mb-1">Invite and manage dashboard users</h2>
      <p className="text-gray-400 text-sm mb-6 max-w-3xl">
        Create invite links, promote users, and control exactly which dashboard tabs each user can see. Non-admin roles can never access the Admin Access tab.
      </p>

      {!isSupabaseConfigured && (
        <div className="mb-6 rounded-lg border border-yellow-500/40 bg-yellow-950/30 p-4 text-sm text-yellow-200">
          <div className="flex items-start gap-2">
            <Shield size={16} className="mt-0.5" />
            <p>
              Supabase is not configured in this frontend yet. Add `REACT_APP_SUPABASE_URL` and `REACT_APP_SUPABASE_ANON_KEY`,
              then run the Supabase migrations before creating production invites or managing users.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="mobile-card bg-gray-900 rounded-xl p-5 border border-gray-800">
          <div className="flex items-center gap-2 text-purple-300 mb-3">
            <UserPlus size={18} />
            <h3 className="font-semibold">Create invite</h3>
          </div>
          <label className="block text-xs text-gray-400 mb-1">Invite email</label>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="agency@example.com"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500 mb-3"
          />

          <label className="block text-xs text-gray-400 mb-1">Role</label>
          <select
            value={role}
            onChange={(event) => setRole(event.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500 mb-3"
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <label className="block text-xs text-gray-400 mb-1">Expires in</label>
          <select
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(event.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500 mb-4"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>

          <button
            onClick={handleCreateInvite}
            disabled={isCreating}
            className="w-full bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
          >
            {isCreating ? 'Creating...' : 'Generate invite link'}
          </button>
        </div>

        <div className="mobile-card lg:col-span-2 bg-gray-900 rounded-xl p-5 border border-gray-800">
          <div className="flex items-center gap-2 text-blue-300 mb-3">
            <Link size={18} />
            <h3 className="font-semibold">Invite link</h3>
          </div>
          {inviteLink ? (
            <>
              <div className="mobile-copy-row flex gap-2">
                <input
                  value={inviteLink}
                  readOnly
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200"
                />
                <button
                  onClick={copyInvite}
                  className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg px-3 py-2 text-sm transition-colors"
                >
                  <Copy size={16} />
                  Copy
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                The invited user signs up from this link. Once they accept it, they appear below for role and tab management.
              </p>
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-700 p-8 text-center text-sm text-gray-500">
              Generate an invite to create a shareable signup link.
            </div>
          )}

          {status && (
            <div className="mt-4 rounded-lg bg-gray-800/70 border border-gray-700 p-3 text-sm text-gray-300">
              {status}
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden mb-6">
        <div className="mobile-panel-header flex items-center justify-between gap-4 border-b border-gray-800 px-5 py-4">
          <div className="flex items-center gap-2 text-green-300">
            <Users size={18} />
            <h3 className="font-semibold">Users and tab visibility</h3>
          </div>
          <button
            onClick={loadUsers}
            disabled={isLoadingUsers}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 disabled:opacity-60"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>

        {isLoadingUsers ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading users...</div>
        ) : users.length ? (
          <div className="table-scroll admin-users-table overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">User</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Role</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Visible tabs</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isSelf = user.id === currentUserId;
                  const currentTabs = user.tabs?.length ? user.tabs : getDefaultTabsForRole(user.role);
                  const isSaving = savingUserId === user.id;
                  const isDeleting = deletingUserId === user.id;

                  return (
                    <tr key={user.id} className="border-b border-gray-800/60 align-top">
                      <td className="px-4 py-4 min-w-[220px]">
                        <div className="font-semibold text-white">
                          {user.name || user.email || 'Unnamed user'}
                          {isSelf && <span className="ml-2 text-xs text-purple-300">You</span>}
                        </div>
                        <div className="text-xs text-gray-500">{user.email || 'No email saved'}</div>
                        <div className="text-xs text-gray-600 mt-1">Joined {formatDate(user.created_at)}</div>
                      </td>
                      <td className="px-4 py-4 min-w-[180px]">
                        <select
                          value={user.role}
                          disabled={isSaving || isSelf}
                          onChange={(event) => handleRoleChange(user, event.target.value)}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500 disabled:opacity-60"
                        >
                          {ROLE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        {isSelf && (
                          <p className="text-xs text-gray-500 mt-2">Your own admin role is protected.</p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-2 max-w-4xl">
                          {DASHBOARD_TABS.map((tab) => {
                            const isAdminTab = tab.key === 'adminAccess';
                            const disabled = isSaving || (isAdminTab && user.role !== 'admin') || (isSelf && isAdminTab);
                            const checked = currentTabs.includes(tab.key) && !(isAdminTab && user.role !== 'admin');

                            return (
                              <label
                                key={tab.key}
                                className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-colors ${
                                  checked
                                    ? 'border-purple-500/50 bg-purple-500/10 text-purple-100'
                                    : 'border-gray-700 bg-gray-800 text-gray-400'
                                } ${disabled ? 'opacity-60' : 'cursor-pointer hover:border-gray-500'}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={disabled}
                                  onChange={() => handleTabToggle(user, tab.key)}
                                  className="accent-purple-500"
                                />
                                {getTabLabel(tab.key)}
                              </label>
                            );
                          })}
                        </div>
                        <div className="text-xs text-gray-600 mt-2">
                          {user.has_custom_tabs ? 'Custom visibility saved' : 'Using role defaults'}
                          {isSaving && <span className="ml-2 text-purple-300">Saving...</span>}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right min-w-[130px]">
                        <button
                          onClick={() => handleDeleteUser(user)}
                          disabled={isSelf || isSaving || isDeleting}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200 transition-colors hover:bg-red-500/20 disabled:cursor-not-allowed disabled:border-gray-700 disabled:bg-gray-800 disabled:text-gray-500"
                        >
                          <Trash2 size={15} />
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                        {isSelf && (
                          <p className="mt-2 text-xs text-gray-600">Protected</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-gray-500">
            No users loaded yet. Accepted invite users will appear here after the Supabase user-management migration is applied.
          </div>
        )}
      </div>

      <div className="table-scroll bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Role</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Allowed</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Blocked</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-800/50">
              <td className="px-4 py-3 font-semibold text-purple-300">Admin/internal</td>
              <td className="px-4 py-3 text-gray-300">Full admin rights, data exports, data editing, invites, user deletion, and user management. Tab visibility can still be customized.</td>
              <td className="px-4 py-3 text-gray-500">None by role.</td>
            </tr>
            <tr className="border-b border-gray-800/50">
              <td className="px-4 py-3 font-semibold text-blue-300">Analytics viewer</td>
              <td className="px-4 py-3 text-gray-300">Any non-admin tabs you tick for that user.</td>
              <td className="px-4 py-3 text-gray-300">Admin access, invites, exports, and add/delete week controls.</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-semibold text-green-300">Model / social manager</td>
              <td className="px-4 py-3 text-gray-300">Non-admin dashboard tabs plus add, sync and remove controls for encrypted social API connections.</td>
              <td className="px-4 py-3 text-gray-300">Admin access, invites, exports, add/delete week controls, and Cloudflare client secrets.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminAccessPanel;
